import { TableModel } from 'app/components/table/table.model';
import { formatCurrency, formatNumTwoDig, formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import { useEffect, useState, useRef } from 'react';

import { IRadioButtonListItem, RadioButtonList } from 'app/components/common/RadioButtonList';
import _ from 'lodash';

import moment from 'moment';
import * as echarts from 'echarts';
import { debounce } from 'lodash';

export interface IScatterPlotVarationModel {
	key: string;
	labelX: string;
	labelY: string;
	pathX: string;
	pathY: string;
	buttonLabel: string;
	plotColor: string | ((model: any) => string);
	plotColorProp?: string;
	domainX?: [any, any];
	ticksX?: number[];
}

export class ScatterPlotVarationModel {
	constructor(opts: IScatterPlotVarationModel) {
		this.key = opts.key;
		this.labelX = opts.labelX;
		this.labelY = opts.labelY;
		this.pathX = opts.pathX;
		this.pathY = opts.pathY;
		this.buttonLabel = opts.buttonLabel;
		this.plotColor = opts.plotColor;
		if (opts.plotColorProp) {
			this.plotColorProp = opts.plotColorProp;
		}
		if (opts.domainX) {
			this.domainX = opts.domainX;
		}
		if (opts.ticksX) {
			this.ticksX = opts.ticksX;
		}
	}
	key: string;
	labelX: string;
	labelY: string;
	pathX: string;
	pathY: string;
	buttonLabel: string;
	plotColor: string | ((model: any) => string);
	plotColorProp?: string;
	domainX: [any, any] = ['auto', 'auto'];
	ticksX: number[] = [];
}

export class ScatterPlotModel<T, P> {
	constructor(opts: any) {
		this.tableModel = opts.tableModel;
		this.variations = opts.variations;
	}

	tableModel: TableModel<T, P>;
	variations: ScatterPlotVarationModel[];
}

export interface IScatterPlotChart<T, P> {
	tableModel: TableModel<T, P>;
	variations: ScatterPlotVarationModel[];
}

export const ScatterPlotChart = observer((props: IScatterPlotChart<any, any>) => {
	const [activeIndex, setActiveIndex] = useState<number | undefined>();

	let defaultVariation = props.variations[0];
	if (moment().month() < 9) {
		// Zeitziel vor Oktober
		const v = props.variations.find((x) => x.key.indexOf('zeit') >= 0);
		if (v) {
			defaultVariation = v;
		}
	}

	const [variation, setVariation] = useState<ScatterPlotVarationModel>(defaultVariation);

	const tm = props.tableModel;
	const buttons = props.variations.map((v) => {
		return { label: v.buttonLabel, value: v.key } as IRadioButtonListItem;
	});

	const formatYAxis = (val: any) => {
		return formatCurrency(val, 0, false);
	};

	const formatXAxis = (val: any) => {
		return val;
	};

	const fillColor = (entry: any) => {
		let plotColor = 'green';
		if (variation && typeof variation.plotColor === 'string') {
			plotColor = variation.plotColor;
		}
		if (variation && typeof variation.plotColor === 'function') {
			const prop = variation.plotColorProp ? variation.plotColorProp : variation.pathX;
			const val = _.get(entry, prop);
			plotColor = variation.plotColor(val);
		}
		return plotColor;
	};

	useEffect(() => {
		const index = tm.data.findIndex((d) => d.agtId === tm.hoverId);
		setActiveIndex(index);
	}, [tm.hoverId, tm.data]);

	const onRadioSelect = (key: any) => {
		setVariation(props.variations.find((v) => v.key === key)!);
	};

	// ECharts setup
	const chartRef = useRef<HTMLDivElement>(null);
	const chartInstanceRef = useRef<echarts.ECharts | null>(null);

	useEffect(() => {
		if (!chartRef.current) return;

		// Initialize chart
		if (!chartInstanceRef.current) {
			chartInstanceRef.current = echarts.init(chartRef.current);
		}

		const chart = chartInstanceRef.current;

		// Prepare data for ECharts
		const scatterData = tm.data.map((entry, index) => {
			const x = _.get(entry, variation.pathX);
			const y = _.get(entry, variation.pathY);
			const color = fillColor(entry);
			const agtName = _.get(entry, 'agt.name') || entry.name || '';
			const agtId = entry.agtId;
			return {
				value: [x, y],
				itemStyle: {
					color: color,
					opacity: index === activeIndex ? 1 : 0.75,
				},
				symbolSize: index === activeIndex ? 24 : 14,
				dataIndex: index,
				agtId: agtId,
				agtName: agtName,
			};
		});

		// Calculate domain
		let xMin: number | 'auto' = 'auto';
		let xMax: number | 'auto' = 'auto';
		if (variation.domainX && variation.domainX[0] !== 'auto') {
			xMin = variation.domainX[0];
		}
		if (variation.domainX && variation.domainX[1] !== 'auto') {
			xMax = variation.domainX[1];
		}

		// Build xAxis configuration
		const xAxisConfig: any = {
			type: 'value',
			name: variation.labelX,
			nameLocation: 'middle',
			nameGap: 30,
			min: xMin,
			max: xMax,
			axisLabel: {
				formatter: (value: number) => `${formatXAxis(value)} %`,
			},
			splitLine: {
				lineStyle: {
					type: 'dashed',
				},
			},
		};

		if (variation.ticksX.length > 0) {
			xAxisConfig.splitNumber = variation.ticksX.length - 1;
			xAxisConfig.minInterval = 1;
		}

		const option: echarts.EChartsOption = {
			grid: {
				top: 10,
				bottom: 50,
				left: 85,
				right: 30,
			},
			xAxis: xAxisConfig,
			yAxis: {
				type: 'value',
				name: variation.labelY,
				nameLocation: 'middle',
				nameGap: 70,
				axisLabel: {
					formatter: (value: number) => formatYAxis(value),
				},
				splitLine: {
					lineStyle: {
						type: 'dashed',
					},
				},
			},
			series: [
				{
					type: 'scatter',
					data: scatterData,
					symbolSize: (data: any) => data.symbolSize || 10,
					itemStyle: {
						opacity: 0.75,
					},
					emphasis: {
						itemStyle: {
							opacity: 1,
						},
					},
				},
			],
			tooltip: {
				trigger: 'item',
				formatter: (params: any) => {
					const data = params.data;
					const agtLabel = data.agtName ? `<strong>${data.agtName} (${data.agtId})</strong>` : '';
					const xValue = `${formatNumTwoDig(data.value[0])} %`;
					const yValue = formatNum(data.value[1]);
					return `${agtLabel}<br/>${variation.labelX}: ${xValue}<br/>${variation.labelY}: ${yValue}`;
				},
			},
		};

		chart.setOption(option);

		// Handle mouse events
		chart.off('mouseover');
		chart.off('mouseout');

		chart.on('mouseover', (params: any) => {
			if (params.componentType === 'series' && params.data) {
				const index = params.data.dataIndex;
				const agtId = tm.data[index].agtId;
				tm.setHoverId(agtId);
				setActiveIndex(index);
			}
		});

		chart.on('mouseout', () => {
			tm.setHoverId();
			setActiveIndex(undefined);
		});

		// Handle resize
		const handleResize = debounce(() => {
			chart.resize();
		}, 300);
		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(chartRef.current);

		return () => {
			resizeObserver.disconnect();
			handleResize.cancel();
		};
	}, [tm.data, variation, activeIndex, formatXAxis, formatYAxis, fillColor, tm]);

	return (
		<>
			<RadioButtonList value={variation.key} buttons={buttons} onSelect={onRadioSelect} />
			<div ref={chartRef} style={{ width: '99%', height: '250px' }} />
		</>
	);
});
